/**
 * 📅 TrackerCalendar — Full-page monthly calendar for Food · Gym · Cardio
 *
 * Owner Feb 2026 pivot: "I wanted a FULL CALENDAR visual... and allow
 * for daily... not just seeing weekly sum but to add input."
 *
 * Reads the same `user_food` + `user_workouts` blobs the FoodLog and
 * MuscleTracker pages sync to. Renders a big month grid with per-day
 * summaries (kcal · lifts · cardio distance). Tap any day → drawer
 * with 3 tabs (Food / Gym / Cardio) and quick-add forms that write
 * BACK to the same blobs (retroactive, future, or today).
 *
 * Any date in ±any range is legal — owner said "add missed days
 * and future days" (Feb 2026 canon).
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X, CalendarDays, Utensils, Dumbbell, Bike, Plus, Trash2, HeartPulse, Copy } from "lucide-react";
import { toast } from "sonner";
import useUserSync from "@/hooks/useUserSync";
import DailyLogPanel from "@/components/DailyLogPanel";

// ── Storage key mirror (must match FoodLog + MuscleTracker) ──
const FOOD_LOG_KEY = "omnimuscle_food_log_v1";
const WORKOUTS_KEY = "omnimuscle_log_v1";
// 🩸 New retroactive vitals blob — separate from server-side /vitals/*
// endpoints so back-filled days don't accidentally re-trigger daily
// streak rewards. Owner can promote entries into the vitals gamification
// module in a follow-up pass.
const VITALS_DAY_KEY = "omnimuscle_vitals_day_log_v1";

// PT-aware day key so today lines up with FoodLog / MuscleTracker
function todayKeyPT() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}
function pad(n) { return String(n).padStart(2, "0"); }
function dateKey(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAY_HEADS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

// Build 6-week grid (42 cells)
function buildMonthGrid(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const startWeekday = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const prevMonthDays = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) {
    const d = prevMonthDays - startWeekday + 1 + i;
    cells.push({ y: month === 0 ? year - 1 : year, m: month === 0 ? 11 : month - 1, d, inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) cells.push({ y: year, m: month, d, inMonth: true });
  const remaining = 42 - cells.length;
  for (let i = 1; i <= remaining; i++) {
    cells.push({ y: month === 11 ? year + 1 : year, m: month === 11 ? 0 : month + 1, d: i, inMonth: false });
  }
  return cells;
}

export default function TrackerCalendar() {
  // 📡 Sync both blobs with the server via the shared hook.
  const [foodLog, setFoodLog]       = useState([]);
  const [workoutLog, setWorkoutLog] = useState([]);
  const [vitalsDay, setVitalsDay]   = useState({});   // { "YYYY-MM-DD": { water_oz, sleep_hours, sleep_quality, weight, weight_unit, mood } }

  // seed from localStorage
  useEffect(() => {
    try { const raw = localStorage.getItem(FOOD_LOG_KEY); if (raw) setFoodLog(JSON.parse(raw)); } catch { /* ignore */ }
    try { const raw = localStorage.getItem(WORKOUTS_KEY); if (raw) setWorkoutLog(JSON.parse(raw)); } catch { /* ignore */ }
    try { const raw = localStorage.getItem(VITALS_DAY_KEY); if (raw) setVitalsDay(JSON.parse(raw)); } catch { /* ignore */ }
  }, []);
  // persist locally on every mutation
  useEffect(() => { try { localStorage.setItem(FOOD_LOG_KEY, JSON.stringify(foodLog)); } catch { /* ignore */ } }, [foodLog]);
  useEffect(() => { try { localStorage.setItem(WORKOUTS_KEY, JSON.stringify(workoutLog)); } catch { /* ignore */ } }, [workoutLog]);
  useEffect(() => { try { localStorage.setItem(VITALS_DAY_KEY, JSON.stringify(vitalsDay)); } catch { /* ignore */ } }, [vitalsDay]);

  // Cross-device sync — food
  useUserSync({
    scope: "food",
    readLocal: () => ({ log: foodLog }),
    writeLocal: (merged) => { if (Array.isArray(merged?.log)) setFoodLog(merged.log); },
    merge: (server, local) => {
      const sLog = Array.isArray(server?.log) ? server.log : [];
      const lLog = Array.isArray(local?.log)  ? local.log  : [];
      const seen = new Set(); const out = [];
      for (const e of [...sLog, ...lLog]) {
        if (!e) continue;
        const key = e.id || `${e.date}|${e.meal || ""}|${e.name || ""}|${e.cal || ""}`;
        if (seen.has(key)) continue; seen.add(key); out.push(e);
      }
      return { log: out };
    },
    deps: [foodLog],
  });
  // Cross-device sync — workouts
  useUserSync({
    scope: "workouts",
    readLocal: () => ({ log: workoutLog }),
    writeLocal: (merged) => { if (Array.isArray(merged?.log)) setWorkoutLog(merged.log); },
    merge: (server, local) => {
      const sLog = Array.isArray(server?.log) ? server.log : [];
      const lLog = Array.isArray(local?.log)  ? local.log  : [];
      const seen = new Set(); const out = [];
      for (const e of [...sLog, ...lLog]) {
        if (!e) continue;
        const key = e.id || `${e.date}|${e.type || "strength"}|${e.name || ""}|${e.sets || ""}|${e.reps || ""}|${e.weight || ""}|${e.duration_min || ""}|${e.distance || ""}`;
        if (seen.has(key)) continue; seen.add(key); out.push(e);
      }
      return { log: out };
    },
    deps: [workoutLog],
  });

  // Cross-device sync — vitals-per-day blob (retroactive-safe)
  useUserSync({
    scope: "vitals_day_log",
    readLocal: () => ({ days: vitalsDay }),
    writeLocal: (merged) => {
      if (merged?.days && typeof merged.days === "object") setVitalsDay(merged.days);
    },
    merge: (server, local) => {
      // Merge by date key — server wins on conflict for fields it set,
      // local wins on newly added keys. Recency-safe for a set-once shape.
      const sDays = (server && typeof server.days === "object") ? server.days : {};
      const lDays = (local && typeof local.days === "object") ? local.days : {};
      const out = { ...sDays };
      for (const [k, v] of Object.entries(lDays)) {
        out[k] = { ...(out[k] || {}), ...v };
      }
      return { days: out };
    },
    deps: [vitalsDay],
  });

  const today = todayKeyPT();
  const [year, month] = useMemo(() => {
    const t = today.split("-"); return [Number(t[0]), Number(t[1]) - 1];
  }, [today]);
  const [viewY, setViewY] = useState(year);
  const [viewM, setViewM] = useState(month);
  const [selectedDate, setSelectedDate] = useState(null); // null = closed drawer

  const cells = useMemo(() => buildMonthGrid(viewY, viewM), [viewY, viewM]);

  // Roll up per-date totals for the whole visible grid
  const dailyTotals = useMemo(() => {
    const totals = {};
    for (const e of foodLog) {
      if (!e?.date) continue;
      totals[e.date] = totals[e.date] || { kcal: 0, food_count: 0, lifts: 0, cardio_km: 0, cardio_min: 0, entries: [] };
      totals[e.date].kcal += Number(e.cal || 0);
      totals[e.date].food_count += 1;
    }
    for (const w of workoutLog) {
      if (!w?.date) continue;
      totals[w.date] = totals[w.date] || { kcal: 0, food_count: 0, lifts: 0, cardio_km: 0, cardio_min: 0, entries: [] };
      if (w.type === "cardio") {
        totals[w.date].cardio_min += Number(w.duration_min || 0);
        if (w.distance_unit === "mi") totals[w.date].cardio_km += Number(w.distance || 0) * 1.60934;
        else totals[w.date].cardio_km += Number(w.distance || 0);
      } else {
        totals[w.date].lifts += 1;
      }
    }
    return totals;
  }, [foodLog, workoutLog]);

  // ── Vitals-day helper — normalize date + persist a partial patch ──
  const setVitalsForDay = (dateKey, patch) => {
    setVitalsDay((prev) => ({
      ...prev,
      [dateKey]: { ...(prev[dateKey] || {}), ...patch, updated_at: new Date().toISOString() },
    }));
  };

  // ── Copy-From-Yesterday: clone every food/strength/cardio entry
  // from selectedDate - 1 into selectedDate. Fresh IDs, `copied_from`
  // set to yesterday's key so audits are possible. Vitals are also
  // copied over (numeric fields) as a starting point the owner can edit.
  const copyFromYesterday = () => {
    if (!selectedDate) return;
    const d = new Date(selectedDate + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() - 1);
    const y = d.getUTCFullYear();
    const m = pad(d.getUTCMonth() + 1);
    const day = pad(d.getUTCDate());
    const yKey = `${y}-${m}-${day}`;

    const clone = (arr) => arr.filter((e) => e.date === yKey).map((e) => ({
      ...e,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      date: selectedDate,
      copied_from: yKey,
    }));

    const newFood = clone(foodLog);
    const newWork = clone(workoutLog);
    const yVitals = vitalsDay[yKey];

    if (!newFood.length && !newWork.length && !yVitals) {
      toast.error(`No entries on ${yKey} to duplicate.`);
      return;
    }

    if (newFood.length) setFoodLog((l) => [...l, ...newFood]);
    if (newWork.length) setWorkoutLog((l) => [...l, ...newWork]);
    if (yVitals) {
      // Only copy numeric / setpoint fields — not booleans, not the
      // updated_at stamp. Owner can tweak the copies inline.
      const { updated_at: _u, ...rest } = yVitals;
      void _u;
      setVitalsForDay(selectedDate, rest);
    }
    const totalCopied = newFood.length + newWork.length + (yVitals ? 1 : 0);
    toast.success(`📋 Duplicated ${totalCopied} entr${totalCopied === 1 ? "y" : "ies"} from ${yKey}`);
  };

  const prevMonth = () => {
    if (viewM === 0) { setViewY(viewY - 1); setViewM(11); } else { setViewM(viewM - 1); }
  };
  const nextMonth = () => {
    if (viewM === 11) { setViewY(viewY + 1); setViewM(0); } else { setViewM(viewM + 1); }
  };
  const jumpToday = () => { setViewY(year); setViewM(month); };

  // ── Selected-date helpers for the drawer
  const foodOnDay      = useMemo(() => foodLog.filter((e) => e.date === selectedDate), [foodLog, selectedDate]);
  const strengthOnDay  = useMemo(() => workoutLog.filter((e) => e.date === selectedDate && e.type !== "cardio"), [workoutLog, selectedDate]);
  const cardioOnDay    = useMemo(() => workoutLog.filter((e) => e.date === selectedDate && e.type === "cardio"), [workoutLog, selectedDate]);

  const addFoodOnDay = ({ name, meal, cal, p, c, f }) => {
    if (!selectedDate) return;
    const clean = String(name || "").trim();
    if (!clean) { toast.error("Enter a food name."); return; }
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      date: selectedDate,
      meal: meal || "meal",
      name: clean,
      cal: Number(cal || 0),
      p: Number(p || 0), c: Number(c || 0), f: Number(f || 0),
      manual: true,
    };
    setFoodLog((l) => [...l, entry]);
    toast.success(`🍽️ Added ${clean} to ${selectedDate}`);
  };
  const addStrengthOnDay = ({ name, muscle, sets, reps, weight, unit }) => {
    if (!selectedDate) return;
    const clean = String(name || "").trim();
    if (!clean) { toast.error("Enter an exercise name."); return; }
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: "strength",
      date: selectedDate,
      name: clean,
      muscle: muscle || "general",
      sets: sets ? Number(sets) : null,
      reps: reps ? Number(reps) : null,
      weight: weight ? Number(weight) : null,
      unit: unit || "lb",
    };
    setWorkoutLog((l) => [...l, entry]);
    toast.success(`💪 Added ${clean} to ${selectedDate}`);
  };
  const addCardioOnDay = ({ cardio_type, name, duration_min, distance, distance_unit, calories }) => {
    if (!selectedDate) return;
    const clean = String(name || cardio_type || "cardio").trim();
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: "cardio",
      date: selectedDate,
      cardio_type: cardio_type || "run",
      name: clean,
      duration_min: duration_min ? Number(duration_min) : null,
      distance: distance ? Number(distance) : null,
      distance_unit: distance_unit || "mi",
      calories: calories ? Number(calories) : null,
    };
    setWorkoutLog((l) => [...l, entry]);
    toast.success(`🚴 Added ${clean} to ${selectedDate}`);
  };
  const removeEntry = (id, source) => {
    if (source === "food") setFoodLog((l) => l.filter((e) => e.id !== id));
    else setWorkoutLog((l) => l.filter((e) => e.id !== id));
    toast.success("Removed.");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="tracker-calendar-root">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-emerald-500/40 text-emerald-300 mb-2">
            <CalendarDays size={14} />
            <span className="font-accent text-[10px] tracking-[0.35em] uppercase">Full Tracker Calendar</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            📅 {MONTH_NAMES[viewM]} <span className="text-emerald-400">{viewY}</span>
          </h1>
          <p className="text-sm text-neutral-400 mt-1">
            Tap any day to log food, exercise, or cardio · back-fill or plan ahead.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prevMonth} data-testid="calendar-prev-month" className="p-2 rounded-lg border border-neutral-800 hover:border-emerald-500 text-neutral-300 hover:text-white">
            <ChevronLeft size={16} />
          </button>
          <button onClick={jumpToday} data-testid="calendar-jump-today" className="px-3 py-2 rounded-lg border border-neutral-800 hover:border-emerald-500 text-xs uppercase tracking-widest font-bold text-neutral-300 hover:text-white">
            Today
          </button>
          <button onClick={nextMonth} data-testid="calendar-next-month" className="p-2 rounded-lg border border-neutral-800 hover:border-emerald-500 text-neutral-300 hover:text-white">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-neutral-400">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> Food (kcal)</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Strength</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-cyan-400" /> Cardio</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-400" /> Vitals</span>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1.5 mb-1.5 text-[10px] uppercase tracking-widest text-neutral-500 text-center">
        {WEEKDAY_HEADS.map((w) => <div key={w}>{w}</div>)}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((c, idx) => {
          const key = dateKey(c.y, c.m, c.d);
          const t = dailyTotals[key];
          const isToday = key === today;
          const isFuture = key > today;
          const isWeekend = ((idx % 7) === 0 || (idx % 7) === 6);
          return (
            <button
              key={idx}
              onClick={() => setSelectedDate(key)}
              data-testid={`calendar-day-${key}`}
              className={`relative aspect-[4/5] rounded-lg border p-1.5 sm:p-2 text-left transition ${
                c.inMonth ? "bg-neutral-950/60 border-neutral-800 hover:border-emerald-500" : "bg-black/30 border-neutral-900/50 opacity-40 hover:opacity-70"
              } ${isToday ? "ring-2 ring-emerald-400" : ""} ${isFuture && c.inMonth ? "text-neutral-500" : ""} ${isWeekend && c.inMonth ? "bg-neutral-950/80" : ""}`}
            >
              <div className={`flex items-baseline justify-between mb-1`}>
                <span className={`text-sm sm:text-base font-bold ${isToday ? "text-emerald-300" : c.inMonth ? "text-white" : "text-neutral-600"}`}>
                  {c.d}
                </span>
                {isToday && (
                  <span className="text-[8px] uppercase tracking-widest text-emerald-400 font-bold">Today</span>
                )}
              </div>
              {t ? (
                <div className="space-y-0.5 text-[9px] sm:text-[10px] leading-tight">
                  {t.kcal > 0 && (
                    <div className="flex items-center gap-1 text-amber-300 truncate">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                      <span className="truncate">{Math.round(t.kcal)} kcal</span>
                    </div>
                  )}
                  {t.lifts > 0 && (
                    <div className="flex items-center gap-1 text-emerald-300 truncate">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                      <span className="truncate">{t.lifts} lift{t.lifts !== 1 && "s"}</span>
                    </div>
                  )}
                  {(t.cardio_km > 0 || t.cardio_min > 0) && (
                    <div className="flex items-center gap-1 text-cyan-300 truncate">
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                      <span className="truncate">
                        {t.cardio_km > 0 ? `${t.cardio_km.toFixed(1)}km` : `${Math.round(t.cardio_min)}min`}
                      </span>
                    </div>
                  )}
                  {vitalsDay[key] && (vitalsDay[key].water_oz || vitalsDay[key].sleep_hours || vitalsDay[key].weight || vitalsDay[key].mood) && (
                    <div className="flex items-center gap-1 text-rose-300 truncate">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0" />
                      <span className="truncate">vitals</span>
                    </div>
                  )}
                </div>
              ) : vitalsDay[key] ? (
                <div className="flex items-center gap-1 text-rose-300 text-[9px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0" />
                  vitals
                </div>
              ) : c.inMonth && !isFuture ? (
                <div className="text-[9px] text-neutral-600 italic">tap +</div>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Day Drawer */}
      {selectedDate && (
        <DayDrawer
          date={selectedDate}
          onClose={() => setSelectedDate(null)}
          food={foodOnDay}
          strength={strengthOnDay}
          cardio={cardioOnDay}
          vitals={vitalsDay[selectedDate] || {}}
          onAddFood={addFoodOnDay}
          onAddStrength={addStrengthOnDay}
          onAddCardio={addCardioOnDay}
          onSetVitals={(patch) => setVitalsForDay(selectedDate, patch)}
          onRemove={removeEntry}
          onCopyYesterday={copyFromYesterday}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// Day Drawer — 4 tabs + quick-add forms + copy-from-yesterday
// ═════════════════════════════════════════════════════════
function DayDrawer({ date, onClose, food, strength, cardio, vitals, onAddFood, onAddStrength, onAddCardio, onSetVitals, onRemove, onCopyYesterday }) {
  const [tab, setTab] = useState("food");
  const hasVitals = !!(vitals?.water_oz || vitals?.sleep_hours || vitals?.weight || vitals?.mood);
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="calendar-day-drawer">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-2xl max-h-[92vh] overflow-hidden flex flex-col rounded-t-2xl sm:rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-br from-neutral-950 via-black to-neutral-950 shadow-2xl"
      >
        <div className="flex items-start justify-between px-5 pt-4 pb-2 border-b border-emerald-500/20">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-300 font-bold">Log for</div>
            <div className="text-xl font-bold text-white">📅 {date}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onCopyYesterday}
              data-testid="drawer-copy-yesterday"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-900/30 text-[10px] uppercase tracking-widest font-bold"
              title="Duplicate every food · strength · cardio · vitals entry from the day before"
            >
              <Copy size={12} /> Copy Yesterday
            </button>
            <button onClick={onClose} data-testid="drawer-close" className="text-neutral-400 hover:text-white p-1"><X size={22} /></button>
          </div>
        </div>

        {/* 📊 Unified Daily Log — retroactively editable, syncs to Kitchen/Gym/AI Coach */}
        <div className="px-5 pt-3" data-testid="tracker-unified-log">
          <DailyLogPanel datePt={date} title={`Unified Log · ${date}`} />
        </div>

        <div className="flex gap-1 px-5 pt-3 border-b border-neutral-900 overflow-x-auto">
          {[
            { k: "food",     label: "Food",     icon: <Utensils size={14} />, count: food.length },
            { k: "strength", label: "Strength", icon: <Dumbbell size={14} />, count: strength.length },
            { k: "cardio",   label: "Cardio",   icon: <Bike size={14} />,     count: cardio.length },
            { k: "vitals",   label: "Vitals",   icon: <HeartPulse size={14} />, count: hasVitals ? 1 : 0 },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`drawer-tab-${t.k}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest font-bold border-b-2 transition whitespace-nowrap ${
                tab === t.k
                  ? "border-emerald-400 text-emerald-300"
                  : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
            >
              {t.icon} {t.label}
              {t.count > 0 && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-300 normal-case">{t.count}</span>}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {tab === "food" && (
            <>
              <FoodQuickAdd onAdd={onAddFood} />
              <EntryList kind="food" entries={food} onRemove={(id) => onRemove(id, "food")} />
            </>
          )}
          {tab === "strength" && (
            <>
              <StrengthQuickAdd onAdd={onAddStrength} />
              <EntryList kind="strength" entries={strength} onRemove={(id) => onRemove(id, "workout")} />
            </>
          )}
          {tab === "cardio" && (
            <>
              <CardioQuickAdd onAdd={onAddCardio} />
              <EntryList kind="cardio" entries={cardio} onRemove={(id) => onRemove(id, "workout")} />
            </>
          )}
          {tab === "vitals" && (
            <VitalsPanel vitals={vitals} onSet={onSetVitals} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Vitals panel — set-once fields per day ─────────────────
function VitalsPanel({ vitals, onSet }) {
  const [water, setWater]           = useState(vitals?.water_oz ?? "");
  const [sleepH, setSleepH]         = useState(vitals?.sleep_hours ?? "");
  const [sleepQ, setSleepQ]         = useState(vitals?.sleep_quality ?? "");
  const [weight, setWeight]         = useState(vitals?.weight ?? "");
  const [weightUnit, setWeightUnit] = useState(vitals?.weight_unit ?? "lb");
  const [mood, setMood]             = useState(vitals?.mood ?? "");

  // Re-hydrate from prop when the drawer opens on a different date /
  // Copy-Yesterday pushes a patch.
  useEffect(() => {
    setWater(vitals?.water_oz ?? "");
    setSleepH(vitals?.sleep_hours ?? "");
    setSleepQ(vitals?.sleep_quality ?? "");
    setWeight(vitals?.weight ?? "");
    setWeightUnit(vitals?.weight_unit ?? "lb");
    setMood(vitals?.mood ?? "");
  }, [vitals]);

  const persist = (patch) => onSet(patch);

  const MOODS = [
    { v: 1, e: "😞", label: "Low" },
    { v: 2, e: "😕", label: "Off" },
    { v: 3, e: "😐", label: "Steady" },
    { v: 4, e: "🙂", label: "Good" },
    { v: 5, e: "😄", label: "Great" },
  ];
  const SLEEP_QUAL = ["restless", "ok", "solid", "deep"];

  return (
    <div className="space-y-3" data-testid="drawer-vitals-panel">
      {/* Water */}
      <div className="rounded-xl border border-cyan-500/30 bg-cyan-950/10 p-3">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold mb-2 flex items-center gap-1.5">
          💧 Water · <span className="text-neutral-400 normal-case">total oz for the day</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={water}
            onChange={(e) => setWater(e.target.value)}
            onBlur={() => persist({ water_oz: water === "" ? null : Number(water) })}
            inputMode="numeric"
            placeholder="ounces (e.g., 96)"
            data-testid="vitals-input-water"
            className="flex-1 px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm focus:border-cyan-500 outline-none"
          />
          <div className="flex gap-1">
            {[8, 16, 32].map((oz) => (
              <button
                key={oz}
                onClick={() => { const next = (Number(water) || 0) + oz; setWater(next); persist({ water_oz: next }); }}
                data-testid={`vitals-water-plus-${oz}`}
                className="px-2 py-1.5 rounded-lg border border-cyan-500/40 text-cyan-300 text-[10px] font-bold uppercase tracking-widest hover:bg-cyan-900/30"
              >+{oz}oz</button>
            ))}
          </div>
        </div>
      </div>

      {/* Sleep */}
      <div className="rounded-xl border border-indigo-500/30 bg-indigo-950/10 p-3">
        <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold mb-2 flex items-center gap-1.5">
          🛌 Sleep · <span className="text-neutral-400 normal-case">hours + quality</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            value={sleepH}
            onChange={(e) => setSleepH(e.target.value)}
            onBlur={() => persist({ sleep_hours: sleepH === "" ? null : Number(sleepH) })}
            inputMode="decimal"
            placeholder="hours (e.g., 7.5)"
            data-testid="vitals-input-sleep-hours"
            className="px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm focus:border-indigo-500 outline-none"
          />
          <select
            value={sleepQ}
            onChange={(e) => { setSleepQ(e.target.value); persist({ sleep_quality: e.target.value }); }}
            data-testid="vitals-input-sleep-quality"
            className="px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm"
          >
            <option value="">— quality —</option>
            {SLEEP_QUAL.map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>
      </div>

      {/* Weight */}
      <div className="rounded-xl border border-amber-500/30 bg-amber-950/10 p-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2 flex items-center gap-1.5">
          ⚖️ Weight · <span className="text-neutral-400 normal-case">morning bodyweight</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            onBlur={() => persist({ weight: weight === "" ? null : Number(weight), weight_unit: weightUnit })}
            inputMode="decimal"
            placeholder="e.g., 175"
            data-testid="vitals-input-weight"
            className="col-span-2 px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm focus:border-amber-500 outline-none"
          />
          <select
            value={weightUnit}
            onChange={(e) => { setWeightUnit(e.target.value); persist({ weight_unit: e.target.value }); }}
            data-testid="vitals-input-weight-unit"
            className="px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm"
          >
            <option value="lb">lb</option>
            <option value="kg">kg</option>
          </select>
        </div>
      </div>

      {/* Mood */}
      <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-950/10 p-3">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-2 flex items-center gap-1.5">
          🎭 Mood · <span className="text-neutral-400 normal-case">how the day felt</span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {MOODS.map((m) => (
            <button
              key={m.v}
              onClick={() => { setMood(m.v); persist({ mood: m.v }); }}
              data-testid={`vitals-mood-${m.v}`}
              className={`p-2 rounded-lg border text-center transition ${
                Number(mood) === m.v
                  ? "border-fuchsia-400 bg-fuchsia-900/40 text-white"
                  : "border-neutral-800 text-neutral-400 hover:border-fuchsia-500/40 hover:text-white"
              }`}
            >
              <div className="text-2xl">{m.e}</div>
              <div className="text-[9px] uppercase tracking-widest">{m.label}</div>
            </button>
          ))}
        </div>
      </div>

      {(vitals?.water_oz || vitals?.sleep_hours || vitals?.weight || vitals?.mood) && (
        <div className="text-[10px] text-neutral-500 text-center italic">
          Vitals auto-save when you tap outside a field or pick a preset.
        </div>
      )}
    </div>
  );
}

// ── Quick-add forms ──────────────────────────────────────
function FoodQuickAdd({ onAdd }) {
  const [name, setName] = useState(""); const [meal, setMeal] = useState("meal");
  const [cal, setCal] = useState(""); const [p, setP] = useState(""); const [c, setC] = useState(""); const [f, setF] = useState("");
  const submit = () => { onAdd({ name, meal, cal, p, c, f }); setName(""); setCal(""); setP(""); setC(""); setF(""); };
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-950/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2">Quick-add Food</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Food name (e.g., Chicken bowl)" data-testid="food-input-name" className="px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm focus:border-amber-500 outline-none" />
        <select value={meal} onChange={(e) => setMeal(e.target.value)} data-testid="food-input-meal" className="px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm">
          <option value="breakfast">Breakfast</option><option value="lunch">Lunch</option><option value="dinner">Dinner</option><option value="snack">Snack</option><option value="meal">Meal</option>
        </select>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        <input value={cal} onChange={(e) => setCal(e.target.value)} placeholder="kcal" inputMode="numeric" data-testid="food-input-cal" className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm" />
        <input value={p} onChange={(e) => setP(e.target.value)} placeholder="P g" inputMode="numeric" className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm" />
        <input value={c} onChange={(e) => setC(e.target.value)} placeholder="C g" inputMode="numeric" className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm" />
        <input value={f} onChange={(e) => setF(e.target.value)} placeholder="F g" inputMode="numeric" className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm" />
      </div>
      <button onClick={submit} data-testid="food-add-btn" className="w-full py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs uppercase tracking-widest">
        <Plus size={12} className="inline mr-1" /> Add
      </button>
    </div>
  );
}
function StrengthQuickAdd({ onAdd }) {
  const [name, setName] = useState(""); const [muscle, setMuscle] = useState("general");
  const [sets, setSets] = useState(""); const [reps, setReps] = useState(""); const [weight, setWeight] = useState(""); const [unit, setUnit] = useState("lb");
  const submit = () => { onAdd({ name, muscle, sets, reps, weight, unit }); setName(""); setSets(""); setReps(""); setWeight(""); };
  return (
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-2">Quick-add Strength</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Exercise (e.g., Bench Press)" data-testid="strength-input-name" className="px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm focus:border-emerald-500 outline-none" />
        <select value={muscle} onChange={(e) => setMuscle(e.target.value)} data-testid="strength-input-muscle" className="px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm">
          {["general","chest","back","shoulders","arms","legs","core","glutes","calves"].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        <input value={sets} onChange={(e) => setSets(e.target.value)} placeholder="Sets" inputMode="numeric" data-testid="strength-input-sets" className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm" />
        <input value={reps} onChange={(e) => setReps(e.target.value)} placeholder="Reps" inputMode="numeric" data-testid="strength-input-reps" className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm" />
        <input value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="Wt" inputMode="numeric" data-testid="strength-input-weight" className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm" />
        <select value={unit} onChange={(e) => setUnit(e.target.value)} className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm">
          <option value="lb">lb</option><option value="kg">kg</option>
        </select>
      </div>
      <button onClick={submit} data-testid="strength-add-btn" className="w-full py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs uppercase tracking-widest">
        <Plus size={12} className="inline mr-1" /> Add
      </button>
    </div>
  );
}
function CardioQuickAdd({ onAdd }) {
  const [cardio_type, setType] = useState("run");
  const [name, setName] = useState("");
  const [duration, setDuration] = useState(""); const [distance, setDistance] = useState("");
  const [distance_unit, setDU] = useState("mi"); const [calories, setCalories] = useState("");
  const submit = () => { onAdd({ cardio_type, name, duration_min: duration, distance, distance_unit, calories }); setName(""); setDuration(""); setDistance(""); setCalories(""); };
  return (
    <div className="rounded-xl border border-cyan-500/30 bg-cyan-950/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold mb-2">Quick-add Cardio</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <select value={cardio_type} onChange={(e) => setType(e.target.value)} data-testid="cardio-input-type" className="px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm">
          {["run","walk","cycle","hike","row","swim","elliptical","stairs","other"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder='Label (e.g. "morning trail")' data-testid="cardio-input-name" className="px-3 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm focus:border-cyan-500 outline-none" />
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        <input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="Min" inputMode="numeric" data-testid="cardio-input-duration" className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm" />
        <input value={distance} onChange={(e) => setDistance(e.target.value)} placeholder="Dist" inputMode="numeric" data-testid="cardio-input-distance" className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm" />
        <select value={distance_unit} onChange={(e) => setDU(e.target.value)} className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm">
          <option value="mi">mi</option><option value="km">km</option>
        </select>
        <input value={calories} onChange={(e) => setCalories(e.target.value)} placeholder="kcal" inputMode="numeric" data-testid="cardio-input-cal" className="px-2 py-2 rounded-lg bg-black/60 border border-neutral-800 text-white text-sm" />
      </div>
      <button onClick={submit} data-testid="cardio-add-btn" className="w-full py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-xs uppercase tracking-widest">
        <Plus size={12} className="inline mr-1" /> Add
      </button>
    </div>
  );
}

function EntryList({ kind, entries, onRemove }) {
  if (!entries.length) return <div className="text-sm text-neutral-500 italic text-center py-4">No entries yet for this day.</div>;
  return (
    <div className="space-y-1.5" data-testid={`drawer-entries-${kind}`}>
      {entries.map((e) => (
        <div key={e.id} className="flex items-center justify-between gap-3 rounded-lg border border-neutral-800 bg-black/40 p-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-white truncate">{e.name}</div>
            <div className="text-[10px] text-neutral-400">
              {kind === "food" && (<>{e.meal && <>{e.meal} · </>}{e.cal ? <>{e.cal} kcal</> : "—"}{e.p ? ` · P${e.p}` : ""}{e.c ? ` · C${e.c}` : ""}{e.f ? ` · F${e.f}` : ""}</>)}
              {kind === "strength" && (<>{e.muscle} · {e.sets || "?"}×{e.reps || "?"}{e.weight ? ` @ ${e.weight}${e.unit || "lb"}` : ""}</>)}
              {kind === "cardio" && (<>{e.cardio_type} · {e.duration_min ? `${e.duration_min}min` : ""}{e.distance ? ` · ${e.distance}${e.distance_unit || "mi"}` : ""}{e.calories ? ` · ${e.calories}kcal` : ""}</>)}
            </div>
          </div>
          <button onClick={() => onRemove(e.id)} data-testid={`entry-remove-${e.id}`} className="p-1.5 rounded-lg border border-red-900/40 text-red-400 hover:text-red-300 hover:border-red-500/60">
            <Trash2 size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}
